import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { GuidelinesHttpService } from 'guideline/domain';
import { catchError, debounceTime, switchMap, tap, throwError } from 'rxjs';
import { ConcurrencyService, GuidelineService, MessagesService } from 'shared/domain';
import { tapAllResponses } from 'shared/util-common';

import { AnchoredComment, CommentHttpService, CommentService } from '@treetop/shared/comments';
import {
  GuidelineEditTrackingDto,
  GuidelineDto,
  ConcurrencyChangesDto,
  Guideline,
  GuidelineReview,
  GuidelineReviewStatus,
} from '@treetop/shared/model';

import { SmartbookHttpService } from '../guideline-view/smartbook.http.service';

export type GuidelineReviewLoadGuidelineOptions = {
  guidelineId: string;
  version?: string;
};

@Injectable()
export class GuidelineReviewPageService {
  private smartbookHttpService = inject(SmartbookHttpService);
  private guidelineService = inject(GuidelineService);
  private guidelineHttpService = inject(GuidelinesHttpService);
  private messagesService = inject(MessagesService);
  private commentsHttpService = inject(CommentHttpService);
  private commentsService = inject(CommentService);
  private router = inject(Router);
  private concurrencyService = inject(ConcurrencyService);

  private updateMessage = signal<string | undefined>(undefined);
  private isRequestChangesProcessing = signal(false);
  private isApproveProcessing = signal(false);
  private isContentChangedDuringReview = signal(false);
  private isContentEditing = signal(false);
  private _loading = signal(new Set<string>());

  public vm = computed(() => ({
    loading: this._loading().size > 0 || this.concurrencyService.loading(),
    disabled: this.concurrencyService.disabled(),
    updateMessage: this.updateMessage(),
    isRequestChangesProcessing: this.isRequestChangesProcessing(),
    isApproveProcessing: this.isApproveProcessing(),
    isContentChangedDuringReview: this.isContentChangedDuringReview(),
    isContentEditing: this.isContentEditing(),
  }));

  public init(guidelineUniqueId: string, version?: string) {
    this.loadGuideline({ guidelineId: guidelineUniqueId, version });
    this.currentlyEditing(guidelineUniqueId);
  }

  public currentlyEditing = rxMethod<string>((guidelineUniqueId$) =>
    guidelineUniqueId$.pipe(
      switchMap((uniqueId) => this.guidelineHttpService.currentlyEditing(uniqueId)),
      tapAllResponses<GuidelineEditTrackingDto[], HttpErrorResponse>(
        (trackingInfo) => {
          if (trackingInfo.length > 0) {
            this.isContentEditing.set(true);
          }
        },
        (error) => {
          console.error('Error fetching currently editing info:', error);
        },
      ),
    ),
  );

  public loadGuideline = rxMethod<GuidelineReviewLoadGuidelineOptions>((options) =>
    options.pipe(
      tap(() => this.toggleLoading('loadGuideline', true)),
      switchMap((options) => this.smartbookHttpService.getSmartbookPreviewByUniqueId(options.guidelineId, options.version)),
      tap((guideline) => {
        this.loadComments({ uniqueId: guideline.uniqueId, version: guideline.version });
      }),
      tapAllResponses<Guideline, HttpErrorResponse>(
        (guideline) => {
          this.guidelineService.setGuideline(guideline);
          this.concurrencyService.init(guideline.uniqueId);

          this.toggleLoading('loadGuideline', false);
        },
        (error) => {
          console.error(error);
          this.toggleLoading('loadGuideline', false);
        },
      ),
    ),
  );

  private loadComments = rxMethod<{ uniqueId: string; version: string }>((params$) =>
    params$.pipe(
      tap(() => this.toggleLoading('loadComments', true)),
      switchMap(({ uniqueId, version }) =>
        this.commentsHttpService.getComments(uniqueId, version).pipe(
          tapAllResponses<AnchoredComment[], HttpErrorResponse>(
            (comments) => {
              this.commentsService.init(uniqueId, version, {
                batchEdit: false,
                readonly: false,
                comments,
              });

              this.toggleLoading('loadComments', false);
            },
            (error) => {
              this.messagesService.showError(undefined, error);
              this.toggleLoading('loadComments', false);
            },
          ),
        ),
      ),
    ),
  );

  public requestChanges = rxMethod<GuidelineDto>((guideline) =>
    guideline.pipe(
      tap(() => this.isRequestChangesProcessing.set(true)),
      debounceTime(300),
      switchMap((guideline) =>
        this.guidelineHttpService.checkConcurrencyChanges(guideline).pipe(
          switchMap((concurrencyResult: ConcurrencyChangesDto) => {
            if (concurrencyResult.hasChanged) {
              this.isContentChangedDuringReview.set(true);
              return throwError(() => new Error());
            } else {
              return this.guidelineHttpService.reviewGuideline(guideline.uniqueId, GuidelineReviewStatus.RequestChanges);
            }
          }),
          catchError((err) => {
            this.isRequestChangesProcessing.set(false);

            if (err instanceof HttpErrorResponse) {
              this.messagesService.showError('GuidelineReview.HttpRequestFailedMessage');
            } else {
              this.messagesService.showError('GuidelineReview.ContentOfReviewedGuidelineIsEdited');
            }

            return [];
          }),
        ),
      ),
      tapAllResponses<GuidelineReview, HttpErrorResponse>(
        () => {
          this.messagesService.showInfo('GuidelineReview.RequestChangesSuccessMessage');
          this.isRequestChangesProcessing.set(false);
          this.router.navigate(['/manage/review']);
        },
        (error: HttpErrorResponse) => {
          this.messagesService.showError('GuidelineReview.RequestChangesErrorMessage', error);
          this.isRequestChangesProcessing.set(false);
        },
      ),
    ),
  );

  public approveGuideline = rxMethod<GuidelineDto>((guideline) =>
    guideline.pipe(
      tap(() => this.isApproveProcessing.set(true)),
      debounceTime(300),
      switchMap((guideline) =>
        this.guidelineHttpService.checkConcurrencyChanges(guideline).pipe(
          switchMap((concurrencyResult: ConcurrencyChangesDto) => {
            if (concurrencyResult.hasChanged) {
              this.isContentChangedDuringReview.set(true);
              return throwError(() => new Error());
            } else {
              return this.guidelineHttpService.reviewGuideline(guideline.uniqueId, GuidelineReviewStatus.Approved);
            }
          }),
          catchError((err) => {
            this.isApproveProcessing.set(false);

            if (err instanceof HttpErrorResponse) {
              this.messagesService.showError('GuidelineReview.HttpRequestFailedMessage');
            } else {
              this.messagesService.showError('GuidelineReview.ContentOfReviewedGuidelineIsEdited');
            }

            return [];
          }),
        ),
      ),
      tapAllResponses<GuidelineReview, HttpErrorResponse>(
        () => {
          this.messagesService.showInfo('GuidelineReview.ApproveSuccessMessage');
          this.isApproveProcessing.set(false);
          this.router.navigate(['/manage/review']);
        },
        (error: HttpErrorResponse) => {
          this.messagesService.showError('GuidelineReview.ApproveErrorMessage', error);
          this.isApproveProcessing.set(false);
        },
      ),
    ),
  );

  private toggleLoading(key: string, isLoading: boolean): void {
    this._loading.update((currentLoadings) => {
      const newLoadings = new Set(currentLoadings);
      if (isLoading) {
        newLoadings.add(key);
      } else {
        newLoadings.delete(key);
      }
      return newLoadings;
    });
  }
}
